const moment = require('moment');
const { isValidDateObject } = require('@/functions/functions');

// These functions deal with formatting Date objects.
// This is the beginning of not adding things to the Date prototype, and calling functions with a Date instance instead.

/**
 * Returns true if all the dates in the given array are the same minute (ignoring seconds and milliseconds).
 *
 * @param {Date[]} dates
 *
 * @return {boolean}
 */
const areAllDatesSameMinute = (dates) => {
    if (dates.length < 1) {
        return true;
    }

    // Set the expected timestamp to that of the first date in the array.
    // Take the millisecond timestamp and round it down to the beginning millisecond of the minute.
    const expectedTimestamp = dates[0].getTime() - (dates[0].getTime() % 60000);

    for (let i = 1; i < dates.length; i++) {
        let thisTimestamp = dates[i].getTime() - (dates[i].getTime() % 60000);
        if (thisTimestamp !== expectedTimestamp) {
            return false;
        }
    }

    return true;
};

/**
 * Returns true if the 2 given timezone have the same time *at present*.
 *
 * @param {string} timezoneA
 * @param {string} timezoneB
 *
 * @returns {boolean}
 */
const areTimezonesCurrentlyEqual = (timezoneA, timezoneB) => {
    return getTimezoneDifference(timezoneA, timezoneB) === 0;
};

/**
 * @param {string} ymdString
 *
 * @return {Date|null}
 */
const fromYMD = (ymdString) => {
    if (ymdString instanceof Date) {
        ymdString = toYMD(ymdString);
    }

    // Using `new Date()` here breaks due to a bug in iOS 14 (maybe 15 too, but not iOS 16). You get "Invalid Date"
    // for new Date('2020-05-20 00:00:00').
    // So use moment to parse the date instead.
    const d = moment(ymdString, 'YYYY-MM-DD').startOf('day').toDate();
    if (!isValidDateObject(d)) {
        return null;
    }

    return d;
};

/**
 * Returns the offset in minutes between 2 timezone names.
 * Note that (by omitting the compareAt parameter) this is doing a comparison at the current time.
 * Various reasons, such as if one timezone does DST and the other does not, may cause the offset between them may
 * change depending on the time of year.
 *
 * @param {string} timezoneA
 * @param {string} timezoneB
 * @param {Date} [compareAt] A date to perform the comparison at (mostly for testing).
 *
 * @returns {number}
 */
const getTimezoneDifference = (timezoneA, timezoneB, compareAt = new Date()) => {
    const tzaDate = new Date(compareAt.toLocaleString('en-US', { timeZone: timezoneA }));
    const tzbDate = new Date(compareAt.toLocaleString('en-US', { timeZone: timezoneB }));

    return (tzaDate.getTime() - tzbDate.getTime()) / 6e4;
};

/**
 * @return {Date}
 */
const getTodayMidnightDate = () => {
    return moment().startOf('day').toDate();
};

/**
 * @return {Date}
 */
const getTomorrowMidnightDate = () => {
    return moment().add(1, 'days').startOf('day').toDate();
};

/**
 * Convert a string like 12:30:45 to a number of milliseconds.
 *
 * @param {string} hmsString A string in the format HH:MM:SS
 *
 * @returns {number}
 */
const hmsStringToMilliseconds = (hmsString) => {
    if (!hmsString) {
        return 0;
    }

    const timeParts = hmsString.split(':');
    if (timeParts.length !== 3) {
        // Incorrect format.
        return 0;
    }

    let ms = 0;

    // Add hours.
    ms += parseInt(timeParts[0], 10) * 3600000;

    // Add minutes.
    ms += parseInt(timeParts[1], 10) * 60000;

    // Add seconds.
    ms += parseInt(timeParts[2], 10) * 1000;

    if (Number.isNaN(ms)) {
        return 0;
    }

    return ms;
};

/**
 * @param {Date} a
 * @param {Date} b
 * @return {boolean}
 */
const isSameDay = (a, b) => {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
};

/**
 * @param {Number} number
 *
 * @return {String}
 */
const padToTwoDigits = (number) => String(number < 10 ? '0' + number : number);

const toHMS = (date) => {
    return padToTwoDigits(date.getHours())
        + ':' + padToTwoDigits(date.getMinutes())
        + ':' + padToTwoDigits(date.getSeconds());
};

/**
 * Returns the date in YYYY-MM format.
 *
 * @param {Date} date
 *
 * @return {string}
 */
const toYM = (date) => {
    return date.getFullYear()
        + '-' + padToTwoDigits(date.getMonth() + 1);
};

/**
 * @param {Date} date
 *
 * @return {string}
 */
const toYMD = (date) => {
    return date.getFullYear()
        + '-' + padToTwoDigits(date.getMonth() + 1)
        + '-' + padToTwoDigits(date.getDate());
};

/**
 * Returns a date in the format YYMMDDHHMMSS (12 digits with no separators).
 *
 * @param {Date} date
 * @param {number} [millisecondDigits] If more uniqueness is required, you can add 1 to 3 digits for the milliseconds.
 *      If 1, an additional digit (tenths of a second) will be added.
 *      If 2, 2 additional digits (hundredths of a second) will be added.
 *      If 3, 3 additional digits (thousandths of a second - the full number of milliseconds) will be added.
 *
 * @returns {string}
 */
const toSquashed = (date, millisecondDigits = 0) => {
    let str = ''
        + ('' + date.getUTCFullYear()).substring(2) // Last 2 digits of the year.
        + padToTwoDigits(date.getUTCMonth() + 1)
        + padToTwoDigits(date.getUTCDate())
        + padToTwoDigits(date.getUTCHours())
        + padToTwoDigits(date.getUTCMinutes())
        + padToTwoDigits(date.getUTCSeconds());

    if (millisecondDigits >= 1) {
        str += ('' + date.getUTCMilliseconds()).substring(0, millisecondDigits);
    }

    return str;
};

module.exports = {
    areAllDatesSameMinute,
    areTimezonesCurrentlyEqual,
    fromYMD,
    getTimezoneDifference,
    getTodayMidnightDate,
    getTomorrowMidnightDate,
    hmsStringToMilliseconds,
    isSameDay,
    toHMS,
    toSquashed,
    toYM,
    toYMD,
};
